import React, { useEffect, useRef, useState } from "react";
import "../affidea-day/affidea-day.css";
import "./PuzzleGame.css";
import { isTabletWidthOnly as isTablet } from "../shared/device";
import { EMPTY, SIZE, canMove, isSolved, move, shuffle, swipeTarget } from "./puzzle";
import { Entry, formatTime, loadEntries, rankEntries, saveEntries, toCsv } from "../affidea-day/ranking";
import EventHeader from "../affidea-day/EventHeader";
import NameRegistration from "../affidea-day/NameRegistration";
import Podium from "../affidea-day/Podium";
import AdminModal from "../affidea-day/AdminModal";

type Phase = "register" | "playing" | "finished";

interface PuzzleGameProps {
  onBackToHome: () => void;
}

const RANKING_KEY = "puzzle-ranking";
const GOAL_IMAGE_PATH = "/images/affidea-day/puzzle-bordado.webp";
const SHUFFLE_MOVES = 20;
// Shorter pointer drags count as a tap on the tile they started on.
const SWIPE_MIN_PX = 20;
// How long the completed image stays on screen before the podium.
const REVEAL_MS = 1500;

const PIECES = Array.from({ length: SIZE * SIZE }, (_, i) => i);
const pct = (n: number) => `${(n * 100) / (SIZE - 1)}%`;

export default function PuzzleGame({ onBackToHome }: PuzzleGameProps) {
  const [phase, setPhase] = useState<Phase>("register");
  const [entries, setEntries] = useState<Entry[]>(() => loadEntries(RANKING_KEY));
  const [name, setName] = useState("");
  const [board, setBoard] = useState(() => shuffle(SHUFFLE_MOVES));
  const [startedAt, setStartedAt] = useState(0);
  const [endedAt, setEndedAt] = useState<number | null>(null);
  const [now, setNow] = useState(0);
  const [adminOpen, setAdminOpen] = useState(false);
  // Enlarged goal image; the clock keeps running while it's open.
  const [peeking, setPeeking] = useState(false);

  const pointerStart = useRef<{ x: number; y: number; pos: number } | null>(null);
  const revealTimeoutRef = useRef<number | null>(null);
  const solved = isSolved(board);

  useEffect(
    () => () => {
      if (revealTimeoutRef.current !== null) window.clearTimeout(revealTimeoutRef.current);
    },
    [],
  );

  useEffect(() => {
    if (phase !== "playing" || endedAt !== null) return;
    const interval = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(interval);
  }, [phase, endedAt]);

  const startGame = (playerName: string) => {
    const startTime = Date.now();
    setName(playerName);
    setBoard(shuffle(SHUFFLE_MOVES));
    setStartedAt(startTime);
    setNow(startTime);
    setEndedAt(null);
    setPeeking(false);
    setPhase("playing");
  };

  const handleTileTap = (pos: number) => {
    if (solved || !canMove(board, pos)) return;
    const next = move(board, pos);
    setBoard(next);
    if (!isSolved(next)) return;

    const end = Date.now();
    setEndedAt(end);
    const updated = [...entries, { name, value: end - startedAt, at: end }];
    saveEntries(RANKING_KEY, updated);
    setEntries(updated);
    revealTimeoutRef.current = window.setTimeout(() => {
      revealTimeoutRef.current = null;
      setPhase("finished");
    }, REVEAL_MS);
  };

  // Taps and swipes both go through pointer events; the board captures the pointer so a
  // swipe that ends outside it still counts.
  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    const pos = Number((event.target as HTMLElement).dataset.pos ?? -1);
    pointerStart.current = { x: event.clientX, y: event.clientY, pos };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    const start = pointerStart.current;
    pointerStart.current = null;
    if (!start) return;
    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;
    if (Math.hypot(dx, dy) >= SWIPE_MIN_PX) handleTileTap(swipeTarget(board, dx, dy));
    else if (start.pos >= 0) handleTileTap(start.pos);
  };

  // Reshuffles but keeps the clock running, so restarting until the board looks easy doesn't pay.
  const reshuffle = () => {
    if (!solved) setBoard(shuffle(SHUFFLE_MOVES));
  };

  const clearRanking = () => {
    saveEntries(RANKING_KEY, []);
    setEntries([]);
  };

  if (!isTablet()) {
    return (
      <div className="unsupported-device">
        <button className="back-button" onClick={onBackToHome} title="Voltar ao início">
          ← Voltar
        </button>
        Este jogo está disponível apenas em tablets.
        <br />
        Aceda através de um dispositivo compatível ou utilize a emulação de dispositivo no
        navegador.
      </div>
    );
  }

  const elapsed = formatTime((endedAt ?? now) - startedAt);

  return (
    <div className="ad-event puzzle">
      {/* Only on registration, like the other games' start screens, so players can't leave mid-round. */}
      {phase === "register" ? (
        <button className="back-button" onClick={onBackToHome} title="Voltar ao início">
          ← Voltar
        </button>
      ) : null}

      <EventHeader adminEnabled={phase === "register"} onAdmin={() => setAdminOpen(true)} />

      {phase === "register" ? (
        <NameRegistration entries={entries} onStart={startGame} />
      ) : phase === "playing" ? (
        <main className="ad-screen puzzle-playing">
          <h1 className="ad-banner">Sliding Puzzle Bordado Albicastrense</h1>
          <section className="puzzle-board-wrap">
            <div
              className="puzzle-board"
              role="application"
              aria-label="Puzzle"
              onPointerDown={handlePointerDown}
              onPointerUp={handlePointerUp}
              onPointerCancel={() => (pointerStart.current = null)}>
              {PIECES.map((piece) => {
                const pos = board.indexOf(piece);
                const isEmpty = piece === EMPTY;
                return (
                  <button
                    key={piece}
                    type="button"
                    className={`puzzle-tile ${isEmpty ? "empty" : ""} ${
                      isEmpty && solved ? "revealed" : ""
                    }`}
                    style={{
                      backgroundImage: `url(${GOAL_IMAGE_PATH})`,
                      backgroundPosition: `${pct(piece % SIZE)} ${pct(Math.floor(piece / SIZE))}`,
                      transform: `translate(${(pos % SIZE) * 100}%, ${Math.floor(pos / SIZE) * 100}%)`,
                    }}
                    data-pos={pos}
                    // Pointer taps are handled above; detail 0 = keyboard activation.
                    onClick={(event) => event.detail === 0 && handleTileTap(pos)}
                    disabled={isEmpty || solved}
                    aria-label={`Peça ${piece + 1}`}
                  />
                );
              })}
            </div>
          </section>
          {/* Not in the mockup, but without the goal image the puzzle is practically unsolvable. */}
          <div className="puzzle-status">
            <button
              type="button"
              className="puzzle-goal"
              onClick={() => setPeeking(true)}
              aria-label="Ver imagem completa">
              <img src={GOAL_IMAGE_PATH} alt="" />
            </button>
            <div className="puzzle-timer" aria-live="off">
              {solved ? "Parabéns!" : "Tempo"} {elapsed}
            </div>
          </div>
          <button type="button" className="ad-pill" onClick={reshuffle} disabled={solved}>
            Recomeçar
          </button>
          <div className="ad-promo">
            <h2>O próximo desafio está noutro totem!</h2>
            <p>
              Testa os teus reflexos no HEALTH RUSH
              <br />e tenta chegar ao topo do ranking.
            </p>
          </div>
        </main>
      ) : (
        <Podium
          ranked={rankEntries(entries, "asc")}
          playerName={name}
          format={formatTime}
          onRestart={() => setPhase("register")}
        />
      )}

      {phase === "playing" && peeking ? (
        <button
          type="button"
          className="puzzle-peek"
          onClick={() => setPeeking(false)}
          aria-label="Fechar imagem">
          <img src={GOAL_IMAGE_PATH} alt="Imagem objetivo" />
          <span className="puzzle-peek-hint">Toca para fechar</span>
        </button>
      ) : null}

      {adminOpen ? (
        <AdminModal
          csv={() => toCsv(entries, "asc", "tempo", formatTime)}
          filePrefix="puzzle"
          onClear={clearRanking}
          onClose={() => setAdminOpen(false)}
        />
      ) : null}
    </div>
  );
}
